import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search,
  SlidersHorizontal,
  X,
  Star,
  MapPin,
  Sparkles,
  ArrowRight,
  AlertCircle,
  SearchX,
} from "lucide-react";
import Navber from "../Components/Navber.jsx";
import "../Styles/Activity.css";
import api from "../Api";
import DOMPurify from "dompurify";

const SKELETON_COUNT = 8;

const createSlug = (title) =>
  (title || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const sanitizeHTML = (html) => ({
  __html: DOMPurify.sanitize(html || ""),
});

const stripHtml = (html) => {
  if (!html) return "";
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  } catch {
    return html;
  }
};

const ActivityGrid = () => {
  const [activities, setActivities] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [ratings, setRatings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const navigate = useNavigate();

  /* ---------- Fetch ---------- */
  useEffect(() => {
    const fetchActivities = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get("/activities");
        if (res?.data?.data) {
          const list = res.data.data;
          setActivities(list);
          setFiltered(list);

          const temp = {};
          list.forEach((a) => {
            temp[a.id] = Math.floor(Math.random() * 2) + 4; // 4–5 demo
          });
          setRatings(temp);
        }
      } catch (err) {
        console.error("Error fetching activities", err);
        setError("Unable to load activities. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchActivities();
  }, []);

  /* ---------- Filter + sort ---------- */
  useEffect(() => {
    let data = [...activities];

    if (search) {
      const q = search.toLowerCase();
      data = data.filter(
        (a) =>
          a.name?.toLowerCase().includes(q) ||
          a.destinations?.some((d) => d.name?.toLowerCase().includes(q))
      );
    }

    if (sortBy === "low-high") {
      data.sort(
        (a, b) =>
          Math.min(...a.destinations.map((d) => d.price)) -
          Math.min(...b.destinations.map((d) => d.price))
      );
    } else if (sortBy === "high-low") {
      data.sort(
        (a, b) =>
          Math.max(...b.destinations.map((d) => d.price)) -
          Math.max(...a.destinations.map((d) => d.price))
      );
    } else if (sortBy === "rating") {
      data.sort((a, b) => (ratings[b.id] || 0) - (ratings[a.id] || 0));
    }

    setFiltered(data);
  }, [search, sortBy, activities, ratings]);

  /* ---------- Handlers ---------- */
  const handleClick = (activity) => {
    const slug = createSlug(activity?.slug || activity?.name || "activity");
    const id = activity?.id;
    const random = Math.floor(100 + Math.random() * 900);
    const urlPart = `${slug}-${id + random}-${random}`;
    navigate(`/activity/${urlPart}`);
  };

  const handleClearSearch = () => setSearch("");
  const handleReset = () => {
    setSearch("");
    setSortBy("");
  };

  const isFilterActive = !!search || !!sortBy;

  return (
    <div className="act-page">
      <div className="act-navbar-slot">
        <Navber />
      </div>

      {/* ---------- Hero ---------- */}
      <section className="act-hero">
        <span className="act-orb act-orb-a" aria-hidden="true" />
        <span className="act-orb act-orb-b" aria-hidden="true" />

        <div className="act-shell">
          <motion.div
            className="act-hero-text"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
          >

            <h1 className="act-title">Discover Unforgettable Activities</h1>
            <p className="act-subtitle">
              Handpicked adventures, cultural trails and once-in-a-lifetime
              experiences across India
            </p>
          </motion.div>
        </div>
      </section>

      {/* ---------- Toolbar ---------- */}
      <div className="act-toolbar">
        <div className="act-shell">
          <div className="act-toolbar-inner">
            <div className="act-search">
              <Search size={16} className="act-search-icon" aria-hidden="true" />
              <input
                type="text"
                className="act-search-input"
                placeholder="Search activities or destinations…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search activities"
              />
              {search && (
                <button
                  type="button"
                  className="act-search-clear"
                  onClick={handleClearSearch}
                  aria-label="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            <div className="act-sort">
              <SlidersHorizontal
                size={15}
                className="act-sort-icon"
                aria-hidden="true"
              />
              <select
                className="act-sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                aria-label="Sort activities"
              >
                <option value="">Recommended</option>
                <option value="low-high">Price: Low → High</option>
                <option value="high-low">Price: High → Low</option>
                <option value="rating">Top Rated</option>
              </select>
            </div>

            <button
              type="button"
              className="act-filter-toggle"
              onClick={() => setFiltersOpen((s) => !s)}
              aria-expanded={filtersOpen}
              aria-label="Toggle sort options"
            >
              <SlidersHorizontal size={16} />
              {isFilterActive && <span className="act-filter-dot" />}
            </button>
          </div>

          {/* Mobile sort drawer */}
          {filtersOpen && (
            <div className="act-mobile-sort">
              <span className="act-mobile-sort-label">Sort by</span>
              <div className="act-mobile-sort-options">
                {[
                  { value: "", label: "Recommended" },
                  { value: "low-high", label: "Price: Low → High" },
                  { value: "high-low", label: "Price: High → Low" },
                  { value: "rating", label: "Top Rated" },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`act-chip ${sortBy === opt.value ? "is-active" : ""}`}
                    onClick={() => {
                      setSortBy(opt.value);
                      setFiltersOpen(false);
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ---------- Results ---------- */}
      <section className="act-results">
        <div className="act-shell">
          <div className="act-results-head">
            <p className="act-results-count">
              {loading ? (
                "Loading activities…"
              ) : (
                <>
                  <strong>{filtered.length}</strong>{' '}
                  {filtered.length === 1 ? "activity" : "activities"} found
                  {search && (
                    <span className="act-results-query"> for “{search}”</span>
                  )}
                </>
              )}
            </p>

            {isFilterActive && !loading && (
              <button
                type="button"
                className="act-results-reset"
                onClick={handleReset}
              >
                Reset
              </button>
            )}
          </div>

          {error ? (
            <div className="act-empty act-empty-error">
              <span className="act-empty-icon">
                <AlertCircle size={26} />
              </span>
              <h3 className="act-empty-title">Something went wrong</h3>
              <p className="act-empty-text">{error}</p>
            </div>
          ) : loading ? (
            <div className="act-grid" aria-busy="true">
              {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                <div key={i} className="act-skeleton" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="act-empty">
              <span className="act-empty-icon">
                <SearchX size={26} />
              </span>
              <h3 className="act-empty-title">No Activities Found</h3>
              <p className="act-empty-text">
                Try adjusting your search or reset the filters to explore
                everything.
              </p>
              <button
                type="button"
                className="act-btn-gold"
                onClick={handleReset}
              >
                Explore All Activities
              </button>
            </div>
          ) : (
            <div className="act-grid">
              {filtered.map((activity, index) => {
                const minPrice = Math.min(
                  ...activity.destinations.map((d) => d.price)
                );
                const ratingValue = ratings[activity.id] || 4;
                const destinationLabel = activity.destinations
                  .map((d) => d.name)
                  .slice(0, 2)
                  .join(", ");
                const hasMoreDest = activity.destinations.length > 2;

                return (
                  <motion.article
                    key={activity.id}
                    className="act-card"
                    onClick={() => handleClick(activity)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleClick(activity);
                      }
                    }}
                    initial={{ opacity: 0, y: 22 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.15 }}
                    transition={{
                      duration: 0.5,
                      delay: Math.min(index, 8) * 0.05,
                    }}
                  >
                    <div className="act-card-media">
                      <img
                        src={activity.image}
                        alt={activity.name}
                        className="act-card-img"
                        loading="lazy"
                        onError={(e) => {
                          e.target.src =
                            "https://via.placeholder.com/600x400?text=No+Image";
                        }}
                      />
                      <span className="act-card-veil" aria-hidden="true" />

                      <span className="act-card-rating">
                        <Star
                          size={11}
                          fill="currentColor"
                          aria-hidden="true"
                        />
                        {ratingValue.toFixed(1)}
                      </span>
                    </div>

                    <div className="act-card-body">
                      <span className="act-card-loc">
                        <MapPin size={11} aria-hidden="true" />
                        {destinationLabel}
                        {hasMoreDest && "…"}
                      </span>

                      <h3 className="act-card-title">{activity.name}</h3>

                      <p
                        className="act-card-desc"
                        dangerouslySetInnerHTML={sanitizeHTML(
                          activity.description
                        )}
                      />

                      <div className="act-card-footer">
                        <div className="act-card-price">
                          <span className="act-card-price-label">From</span>
                          <span className="act-card-price-value">
                            ₹{minPrice.toLocaleString()}
                          </span>
                        </div>

                        <span className="act-card-cta">
                          Explore
                          <ArrowRight size={13} aria-hidden="true" />
                        </span>
                      </div>
                    </div>
                  </motion.article>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ActivityGrid;